/**
 * OpenItemsPage — OP-Dashboard (Offene Posten)
 *
 * Shows all non-cancelled invoices for the current user with their computed
 * payment status (open / partially_paid / paid / overdue).
 *
 * Features:
 *   - Loads invoices scoped to `currentUser.uid` (M0-konform).
 *   - Loads all payments for the user via `paymentsApi.getPaymentsForUser`.
 *   - Storno-Filter (K2): excludes invoices where `isCancellation == true` OR
 *     `isCancelled == true`.  These carry no open balance.
 *   - Payment-capture modal: creates a new `payments` doc (M0-scoped).
 *   - W3 decision: no update/delete UI in M1c — only creation.  Audit log
 *     (W3) is therefore NOT triggered in M1c.  Delete/Update remain for M9.
 *   - KEINE "GoBD-konform"-Aussage in der UI (DoD-Auflage M1c).
 *
 * @module OpenItemsPage
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconOpenItems, IconEuro, IconNotifications } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useIsReadOnly } from '../hooks/useIsReadOnly';
import { db } from '../firebaseConfig';
import Button from '../components/common/Button';
import RowAction from '../components/common/RowAction';
import { RowActionsMenu, RowActionsMenuItem } from '../components/common/RowActionsMenu';
import { formatDate } from '../utils/formatters';
import { INVOICE_FORM_ROUTE } from '../config/cockpitAccess';
import { canUseDunning, canUseDunningAutomation } from '../config/dunningAccess';
// Type-only import (erased at compile) — no runtime coupling to the lazy panel.
import type { DunningProposalInvoice } from '../components/dunning/DunningAutomationPanel';
import { canUseOpenItems } from '../config/openItemsAccess';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { Spinner } from '../components/common/Spinner';
import { eurosToCents, centsToEuros } from '../utils/moneyUtils';
import {
  computeOpenItemStatus,
  ComputeOpenItemStatusResult,
} from '../utils/openItemStatus';
import {
  getPaymentsForUser,
  createPayment,
} from '../api/paymentsApi';
import { Payment, PaymentMethod } from '../types/paymentTypes';
import { DunningEvent, DunningDelivery } from '../types/dunningTypes';
import {
  getAllDunningEventsForUser,
  getAllDunningDeliveriesForUser,
  callSendDunningEmail,
} from '../api/dunningApi';
import logger from '../utils/logger';
import ClosedLoopHints from '../components/moat/ClosedLoopHints';
import FeatureTeaser from '../components/premium/FeatureTeaser';
import GraceCountdownBanner from '../components/premium/GraceCountdownBanner';

// ─── PR-T6 (D7, §3.3) — Take-away-Upsell-Teaser ────────────────────────────────

/**
 * Upsell-Teaser für nicht-berechtigte Nutzer (`!canOpenItems`, PR-T5
 * Take-away-Gate). Dieser Zweig ist NUR erreichbar, wenn `TIER_V2_FLAG` aktiv
 * ist (s. `canUseOpenItems`, `config/openItemsAccess.ts`) — bei Flag AUS
 * liefert der Helfer immer `true`, der Zweig wird nie betreten (Null-Delta).
 * Ersetzt den vormaligen `t('lockedUpsell', {defaultValue: …})`-Platzhalter
 * (T5-🟢: fehlende echte i18n-Keys) durch ein wertkommunizierendes
 * FeatureTeaser-Primitive mit konkreten Value-Props in allen 6 Sprachen.
 */
const OpenItemsTeaser: React.FC = () => (
  <FeatureTeaser
    icon={<IconOpenItems size={30} aria-hidden />}
    badgeKey="openItems.teaserBadge"
    badgeDefault="Premium-Funktion"
    titleKey="openItems.teaserTitle"
    titleDefault="Offene-Posten-Dashboard"
    descriptionKey="openItems.teaserDescription"
    descriptionDefault="Behalten Sie unbezahlte und teilweise bezahlte Rechnungen jederzeit im Blick — inklusive Mahnstatus und Zahlungsfristen auf einen Blick."
    highlights={[
      { labelKey: 'openItems.teaserHighlight1', defaultLabel: 'Alle offenen Rechnungen auf einen Blick' },
      {
        labelKey: 'openItems.teaserHighlight2',
        defaultLabel: 'Automatische Fristen- und Mahnstatus-Übersicht',
      },
      { labelKey: 'openItems.teaserHighlight3', defaultLabel: 'Nie wieder eine Zahlung übersehen' },
    ]}
    ctaKey="openItems.teaserCta"
    ctaDefault="Jetzt Premium werden"
    promotionId="feature_teaser_open_items"
    creativeSlot="page"
  />
);

// ─── Dunning feature (flag-gated: REACT_APP_DUNNING_ENABLED) ──────────────────

const DUNNING_ENABLED = process.env.REACT_APP_DUNNING_ENABLED === 'true';

// Lazy-load dunning components only when the feature is enabled
// (avoids bundling them when the flag is off)
const DunningModal = DUNNING_ENABLED
  ? React.lazy(() => import('../components/dunning/DunningModal'))
  : null;

const DunningPolicySettings = DUNNING_ENABLED
  ? React.lazy(() => import('../components/dunning/DunningPolicySettings'))
  : null;

// MA-6: Halbautomatik-Vorschlags-Panel. Lazy an dasselbe Build-Flag gebunden wie
// die übrigen Mahn-Komponenten (isolatedModules-Lazy-Require-Muster); die
// tatsächliche Sichtbarkeit entscheidet zur Laufzeit `canUseDunningAutomation`
// (Halbautomatik-Flag ∧ Ultra/Bank-Abgleich ∧ Mahnwesen-Zugriff, fail-closed).
const DunningAutomationPanel = DUNNING_ENABLED
  ? React.lazy(() => import('../components/dunning/DunningAutomationPanel'))
  : null;

// ─── Local types ──────────────────────────────────────────────────────────────

/** Raw Firestore invoice document shape relevant to the OP dashboard. */
interface InvoiceDoc {
  id: string;
  userId: string;
  invoiceNumber: string;
  invoiceDate: string | Timestamp;
  buyerName: string;
  totalAmount: number;
  currency?: string;
  /** Top-level dueDate written by M1b saveInvoiceRecord fix (YYYY-MM-DD). */
  dueDate?: string | null;
  isCancelled?: boolean;
  isCancellation?: boolean;
  /** rawData contains skonto, dueDate fallback, etc. */
  rawData?: {
    invoiceDate?: string;
    dueDate?: string;
    paymentTerms?: { dueDate?: string };
    skonto?: { percentage?: number; days?: number } | null;
    /**
     * Buyer contact block from the parsed invoice. `buyer.email` is the dunning
     * recipient (MA-6 gate condition 9 / `sendDunningEmail`) — absent ⇒ the
     * Halbautomatik proposal is shown as `NEEDS_EMAIL` (manual send only).
     */
    buyer?: { email?: string | null };
  };
  /**
   * Buyer record stored directly on the Firestore invoice document.
   * `companyName` (if present and non-empty) is used as a B2B heuristic in
   * the dunning modal — the user must confirm or change the debtor type.
   */
  buyer?: {
    companyName?: string | null;
    name?: string;
  };
}

/** Enriched row for the OP table. */
interface OpenItemRow {
  invoice: InvoiceDoc;
  payments: Payment[];
  computed: ComputeOpenItemStatusResult;
  grandTotalCents: number;
  paidSumCents: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns today's date as an ISO string YYYY-MM-DD (local timezone). */
function getTodayISO(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Extracts a YYYY-MM-DD string from a Timestamp, string, or undefined. */
function toDateISO(value: string | Timestamp | null | undefined): string | null {
  if (!value) return null;
  if (typeof value === 'string') {
    // Accept YYYY-MM-DD or ISO 8601 with time
    return value.slice(0, 10);
  }
  if (value instanceof Timestamp) {
    const d = value.toDate();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const ddd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${ddd}`;
  }
  return null;
}

/**
 * Extracts dueDate from an InvoiceDoc.
 * Priority: top-level `dueDate` (written by M1b) →
 *   `rawData.dueDate` → `rawData.paymentTerms.dueDate` → null.
 */
function extractDueDate(inv: InvoiceDoc): string | null {
  if (inv.dueDate) return inv.dueDate;
  if (inv.rawData?.dueDate) return inv.rawData.dueDate;
  if (inv.rawData?.paymentTerms?.dueDate) return inv.rawData.paymentTerms.dueDate;
  return null;
}

/**
 * Extracts invoiceDate from an InvoiceDoc.
 * Priority: top-level `invoiceDate` (Timestamp or string) →
 *   `rawData.invoiceDate` → today (safe fallback).
 */
function extractInvoiceDate(inv: InvoiceDoc): string {
  const topLevel = toDateISO(inv.invoiceDate);
  if (topLevel) return topLevel;
  if (inv.rawData?.invoiceDate) return inv.rawData.invoiceDate.slice(0, 10);
  return getTodayISO();
}

/** Returns the ISO date of the most recent payment, or null. */
function latestPaymentDate(payments: Payment[]): string | null {
  if (payments.length === 0) return null;
  const dates = payments.map((p) => {
    const raw = p.paidAt;
    if (typeof raw === 'string') return raw.slice(0, 10);
    if (raw instanceof Timestamp) return toDateISO(raw) ?? getTodayISO();
    return getTodayISO();
  });
  return dates.sort().at(-1) ?? null;
}

/**
 * Sort order for OP rows.
 * Priority: overdue first, then open/partially_paid, then paid.
 * Within same group: by dueDate ascending (earliest first), null at end.
 */
function sortRows(rows: OpenItemRow[]): OpenItemRow[] {
  const priority: Record<string, number> = {
    overdue: 0,
    open: 1,
    partially_paid: 2,
    paid: 3,
  };
  return [...rows].sort((a, b) => {
    const pa = priority[a.computed.status] ?? 99;
    const pb = priority[b.computed.status] ?? 99;
    if (pa !== pb) return pa - pb;
    const da = extractDueDate(a.invoice);
    const db2 = extractDueDate(b.invoice);
    if (da === db2) return 0;
    if (da == null) return 1;
    if (db2 == null) return -1;
    return da < db2 ? -1 : 1;
  });
}

/** Formats a euro-cent value to a locale string. */
function formatEuros(cents: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(centsToEuros(cents));
}

/**
 * Formats a Firestore Timestamp for display in the dunning history sub-row.
 * Delegates to the central `formatDate` (M-B, architecture-ux-density-v2.md
 * §5.6) — `Timestamp` matches `DateInput`'s structural `{ toDate }` case, so
 * this is a thin call-site wrapper, not a re-implementation. `locale` here is
 * already the page-level BCP-47 tag ('de-DE'/'en-GB'), which `formatDate`
 * accepts as-is.
 */
function formatDunningDate(ts: Timestamp | undefined, locale: string): string {
  return formatDate(ts, { locale });
}

// ─── Payment capture modal ────────────────────────────────────────────────────

interface PaymentModalProps {
  invoiceId: string;
  invoiceNumber: string;
  openAmountCents: number;
  onClose: () => void;
  onSuccess: () => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  invoiceId,
  invoiceNumber,
  openAmountCents,
  onClose,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation('openItems');
  const today = getTodayISO();
  const [amountEuros, setAmountEuros] = useState<string>('');
  const [paidAt, setPaidAt] = useState<string>(today);
  const [method, setMethod] = useState<PaymentMethod>('bank');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openAmountEuros = centsToEuros(openAmountCents);
  const parsedAmount = parseFloat(amountEuros.replace(',', '.'));
  const parsedCents = Number.isFinite(parsedAmount)
    ? eurosToCents(parsedAmount)
    : 0;
  const isOverpayment =
    Number.isFinite(parsedAmount) && parsedCents > openAmountCents;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!Number.isFinite(parsedAmount) || parsedCents <= 0) {
      setError(t('modal.errorAmountInvalid'));
      return;
    }

    setSaving(true);
    try {
      await createPayment({
        invoiceId,
        amountCents: parsedCents,
        paidAt,
        method,
        source: 'manual',
        reconciled: false,
      });
      onSuccess();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const locale = i18n.language.startsWith('de') ? 'de-DE' : 'en-GB';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-modal-title"
    >
      <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-md mx-4 border border-hairline">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
          <h2
            id="payment-modal-title"
            className="text-lg font-semibold text-primary"
          >
            {t('modal.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-muted hover:text-primary transition"
            aria-label={t('modal.close')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Invoice reference */}
          <p className="text-sm text-muted">
            {t('modal.invoiceRef')}{' '}
            <span className="font-medium text-secondary">{invoiceNumber}</span>
          </p>

          {/* Amount field */}
          <div>
            <label htmlFor="payment-amount" className="block text-sm font-medium text-secondary mb-1">
              {t('modal.labelAmount')}{' '}
              <span className="text-faint font-normal">
                ({t('modal.openBalance')}: {formatEuros(openAmountCents, locale)})
              </span>
            </label>
            <input
              id="payment-amount"
              type="text"
              inputMode="decimal"
              value={amountEuros}
              onChange={(e) => setAmountEuros(e.target.value)}
              placeholder={openAmountEuros.toFixed(2)}
              className="w-full bg-surface-inset border border-strong rounded-lg px-3 py-2 text-primary placeholder:text-faint focus:outline-none focus:border-indigo-500"
              required
            />
            {isOverpayment && (
              <p className="mt-1 text-sm text-[var(--tone-warning-fg)]">
                {t('modal.warningOverpayment', {
                  amount: formatEuros(parsedCents - openAmountCents, locale),
                })}
              </p>
            )}
          </div>

          {/* Date field */}
          <div>
            <label htmlFor="payment-date" className="block text-sm font-medium text-secondary mb-1">
              {t('modal.labelDate')}
            </label>
            <input
              id="payment-date"
              type="date"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              className="w-full bg-surface-inset border border-strong rounded-lg px-3 py-2 text-primary focus:outline-none focus:border-indigo-500"
              required
            />
          </div>

          {/* Method selector */}
          <div>
            <label htmlFor="payment-method" className="block text-sm font-medium text-secondary mb-1">
              {t('modal.labelMethod')}
            </label>
            <select
              id="payment-method"
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
              className="w-full bg-surface-inset border border-strong rounded-lg px-3 py-2 text-primary focus:outline-none focus:border-indigo-500"
            >
              <option value="bank">{t('modal.methodBank')}</option>
              <option value="cash">{t('modal.methodCash')}</option>
              <option value="card">{t('modal.methodCard')}</option>
              <option value="manual">{t('modal.methodManual')}</option>
            </select>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-[var(--tone-danger-fg)] bg-[var(--tone-danger-bg)] rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-secondary hover:text-primary transition"
            >
              {t('modal.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-800 disabled:cursor-not-allowed text-white rounded-lg transition"
            >
              {saving ? t('modal.saving') : t('modal.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_CLASSES: Record<string, string> = {
  open: 'bg-[var(--tone-info-bg)] text-[var(--tone-info-fg)] border border-[var(--tone-info-border)]',
  partially_paid: 'bg-[var(--tone-warning-bg)] text-[var(--tone-warning-fg)] border border-[var(--tone-warning-border)]',
  paid: 'bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)] border border-[var(--tone-success-border)]',
  overdue: 'bg-[var(--tone-danger-bg)] text-[var(--tone-danger-fg)] border border-[var(--tone-danger-border)]',
};

interface StatusBadgeProps {
  status: ComputeOpenItemStatusResult['status'];
  isOverpaid: boolean;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, isOverpaid }) => {
  const { t } = useTranslation('openItems');
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASSES[status] ?? ''}`}
    >
      {t(`status.${status}`)}
      {isOverpaid && (
        <span className="ml-1 text-[var(--tone-warning-fg)]" title={t('status.overpaidHint')}>
          &#43;
        </span>
      )}
    </span>
  );
};

// ─── Dunning history chip ─────────────────────────────────────────────────────

/**
 * Per-dunning-event chip shown in the OP-table history sub-row.
 *
 * Displays: level badge, creation date, amount due, delivery status, and
 * (when a stored PDF exists) a "Send by e-mail" button.
 *
 * Manages its own transient send-state ('idle' | 'sending' | 'success' | 'error').
 * On success, calls `onSendSuccess()` after a short delay so the parent can reload
 * data and display the newly created `dunning_deliveries` record.
 *
 * Button is disabled / hidden when `pdfStoragePath` is absent (legacy events that
 * predate AP3 PDF-storage, or events from backends that don't expose the header).
 */
interface DunningHistoryChipProps {
  evt: DunningEvent;
  /** Latest delivery for this event, or undefined if none exists yet. */
  delivery: DunningDelivery | undefined;
  locale: string;
  /** Called after a successful e-mail send to trigger a data reload. */
  onSendSuccess: () => void;
}

const DunningHistoryChip: React.FC<DunningHistoryChipProps> = ({
  evt,
  delivery,
  locale,
  onSendSuccess,
}) => {
  const { t } = useTranslation('dunning');
  // M11b-3: advisor (read-only) sieht den Mahn-Versand-Button nicht.
  const isReadOnly = useIsReadOnly();
  const [sendStatus, setSendStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [sendError, setSendError] = useState<string | null>(null);

  const hasPdf = Boolean(evt.pdfStoragePath);

  const handleSend = async () => {
    if (!evt.id || !hasPdf) return;
    setSendStatus('sending');
    setSendError(null);
    try {
      await callSendDunningEmail({ dunningEventId: evt.id });
      setSendStatus('success');
      // Brief success feedback, then reload parent data so delivery status appears.
      setTimeout(() => {
        onSendSuccess();
      }, 900);
    } catch (err: unknown) {
      // Log without recipient PII (Golden Rule).
      logger.warn(
        `DunningHistoryChip: sendDunningEmail failed for event ${evt.id ?? 'unknown'}: ` +
        (err instanceof Error ? err.message : String(err)),
      );
      setSendStatus('error');
      setSendError(err instanceof Error ? err.message : t('history.sendError'));
    }
  };

  return (
    <div className="inline-flex flex-col gap-1 text-xs bg-surface-raised border border-hairline rounded-lg px-2.5 py-1.5 text-secondary min-w-[180px]">
      {/* Top line: level badge · created date · amount */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-medium text-accent-ink shrink-0">
          {t('history.levelBadge', { n: evt.level })}
        </span>
        <span className="text-gray-600">·</span>
        <span className="shrink-0">
          {t('history.createdOn', {
            date: formatDunningDate(evt.createdAt as Timestamp, locale),
          })}
        </span>
        <span className="text-gray-600">·</span>
        <span className="text-secondary shrink-0 font-medium">
          {formatEuros(evt.amountDueCents, locale)}
        </span>
      </div>

      {/* Delivery status line */}
      <div className="text-faint">
        {delivery
          ? t('history.deliveredOn', {
              date: formatDunningDate(delivery.sentAt as Timestamp, locale),
              recipient: delivery.recipient,
            })
          : hasPdf
          ? t('history.notSent')
          : t('history.noPdf')}
      </div>

      {/* Send button — only shown when a PDF is stored and not yet successfully sent
          M11b-3: für advisor (read-only) ausgeblendet (Versand = mutierend) */}
      {!isReadOnly && hasPdf && sendStatus !== 'success' && (
        <button
          type="button"
          onClick={handleSend}
          disabled={sendStatus === 'sending'}
          className="self-start mt-0.5 text-xs px-2.5 py-0.5 bg-[var(--tone-accent-bg)] hover:bg-[var(--tone-accent-bg)] hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed text-accent-ink border border-[var(--tone-accent-border)] rounded transition whitespace-nowrap"
          aria-label={t('history.sendButton')}
        >
          {sendStatus === 'sending' ? t('history.sending') : t('history.sendButton')}
        </button>
      )}

      {/* Brief success feedback */}
      {sendStatus === 'success' && (
        <span className="text-[var(--tone-success-fg)] text-xs">{t('history.sendSuccess')}</span>
      )}

      {/* Error feedback */}
      {sendStatus === 'error' && (
        <span className="text-[var(--tone-danger-fg)] text-xs">
          {sendError ?? t('history.sendError')}
        </span>
      )}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

type StatusFilter = 'all' | 'open' | 'partially_paid' | 'paid' | 'overdue';

const OpenItemsPage: React.FC = () => {
  const { t, i18n } = useTranslation(['openItems', 'dunning']);
  const { currentUser, effectiveScopeUid, sellerData } = useAuth();
  const navigate = useNavigate();
  // M11b-3: advisor (read-only) sieht keine Zahlungs-/Mahn-Aktionen
  // (beide mutierend). Die OP-Liste selbst (Lesevorgang) bleibt sichtbar.
  const isReadOnly = useIsReadOnly();

  // M-A1 (§6.3): Advisor-im-Mandanten-Modus wird nie owner-tier-gegatet. Signal
  // ist der Scope-Vergleich (VERBATIM `DatevExportPage.tsx:106`), NICHT
  // `useIsReadOnly()`/`useIsAdvisorContext()` — nur der Fremd-Tenant-Scope zählt,
  // der eigene Kanzlei-Scope bleibt Owner-Pfad.
  const isOwnScope = !!currentUser && effectiveScopeUid === currentUser.uid;

  // Take-away-Gates (PR-T5, §3.3). Bei TIER_V2_FLAG=false byte-identisch zum
  // IST: `canDunning === DUNNING_ENABLED` (dieselbe Env-Var) und `canOpenItems`
  // ist immer true (das OP-Dashboard war bisher ungegatet). Der MODUL-Scope
  // (`DUNNING_ENABLED` + die lazy-`require`-Bindings oben) bleibt bewusst auf
  // dem reinen Build-Flag — dort gibt es keine `sellerData`. Einmal pro Render
  // berechnet, in loadData + im Render wiederverwendet.
  const canDunning = canUseDunning(sellerData, !isOwnScope);
  const canOpenItems = canUseOpenItems(sellerData, !isOwnScope);

  const [rows, setRows] = useState<OpenItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Payment modal state
  const [modalInvoice, setModalInvoice] = useState<{
    id: string;
    invoiceNumber: string;
    openAmountCents: number;
  } | null>(null);

  // Dunning modal state (only when DUNNING_ENABLED)
  const [dunningInvoice, setDunningInvoice] = useState<{
    id: string;
    invoiceNumber: string;
    invoiceDateISO: string;
    dueDateISO: string | null;
    openAmountCents: number;
    buyerCompanyName?: string | null;
  } | null>(null);

  // Dunning history state — Map<invoiceId, DunningEvent[]> sorted by level/createdAt.
  // Only populated when DUNNING_ENABLED; stays as empty Map otherwise.
  const [dunningEventsByInvoice, setDunningEventsByInvoice] = useState<Map<string, DunningEvent[]>>(
    new Map(),
  );

  // Dunning deliveries state — Map<dunningEventId, DunningDelivery> (latest delivery per event).
  // Only populated when DUNNING_ENABLED; stays as empty Map otherwise.
  const [dunningDeliveriesByEventId, setDunningDeliveriesByEventId] = useState<
    Map<string, DunningDelivery>
  >(new Map());

  const locale = i18n.language.startsWith('de') ? 'de-DE' : 'en-GB';
  const today = getTodayISO();

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError(null);
    try {
      // 1. Load all invoices scoped to the current user
      const invoicesRef = collection(db, 'invoices');
      const invoicesQuery = query(
        invoicesRef,
        where('userId', '==', effectiveScopeUid),
        orderBy('createdAt', 'desc'),
      );
      const snap = await getDocs(invoicesQuery);

      const rawInvoices: InvoiceDoc[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<InvoiceDoc, 'id'>),
      }));

      // 2. Storno-Filter (K2, PFLICHT):
      //    - isCancellation == true: This doc IS a cancellation note — it has no
      //      own open balance and must never appear in the OP view.
      //    - isCancelled == true: The original invoice was cancelled — it is
      //      settled by the cancellation note, not by a payment.
      //    Field verification: both fields are set by useInvoiceSubmission.ts
      //    (isCancellation) and InvoiceArchive.tsx confirms both are present in
      //    live Firestore data (confirmed via Grep on invoiceTypes.ts lines 357-358).
      const activeInvoices = rawInvoices.filter(
        (inv) => !inv.isCancellation && !inv.isCancelled,
      );

      // 3. Load all payments for this user (one round-trip)
      const allPayments = await getPaymentsForUser(effectiveScopeUid);

      // 4. Group payments by invoiceId
      const paymentsByInvoice = new Map<string, Payment[]>();
      for (const p of allPayments) {
        const arr = paymentsByInvoice.get(p.invoiceId) ?? [];
        arr.push(p);
        paymentsByInvoice.set(p.invoiceId, arr);
      }

      // 5. Compute status for each active invoice
      const computedRows: OpenItemRow[] = activeInvoices.map((inv) => {
        const invoicePayments = paymentsByInvoice.get(inv.id) ?? [];
        const grandTotalCents = eurosToCents(inv.totalAmount ?? 0);
        const paymentsCents = invoicePayments.map((p) => p.amountCents);
        const paidSumCents = paymentsCents.reduce((a, b) => a + b, 0);

        const invoiceDateISO = extractInvoiceDate(inv);
        const dueDateISO = extractDueDate(inv);
        const paymentDateISO = latestPaymentDate(invoicePayments);

        const rawSkonto = inv.rawData?.skonto;
        const skontoPercentage =
          rawSkonto?.percentage != null ? rawSkonto.percentage : null;
        const skontoDays =
          rawSkonto?.days != null ? rawSkonto.days : null;

        const computed = computeOpenItemStatus({
          storedGrandTotalCents: grandTotalCents,
          paymentsCents,
          invoiceDateISO,
          paymentDateISO,
          dueDateISO,
          todayISO: today,
          skontoPercentage,
          skontoDays,
        });

        return { invoice: inv, payments: invoicePayments, computed, grandTotalCents, paidSumCents };
      });

      setRows(sortRows(computedRows));

      // 6. Load dunning events + deliveries for history sub-rows (nur wenn der
      //    Nutzer das Mahnwesen nutzen darf — `canDunning`, Take-away-Gate PR-T5;
      //    bei TIER_V2_FLAG=false identisch zum bisherigen `DUNNING_ENABLED`).
      //    Both fetches run in parallel via Promise.all (single-field auto-index, no composite
      //    index needed for either query).  Non-critical: a failure here does not prevent the
      //    OP table from rendering.
      if (canDunning) {
        try {
          const [allEvents, allDeliveries] = await Promise.all([
            getAllDunningEventsForUser(effectiveScopeUid),
            getAllDunningDeliveriesForUser(effectiveScopeUid),
          ]);

          // Build invoiceId → DunningEvent[] map (sorted by level asc, then createdAt asc).
          const eventMap = new Map<string, DunningEvent[]>();
          for (const evt of allEvents) {
            const arr = eventMap.get(evt.invoiceId) ?? [];
            arr.push(evt);
            eventMap.set(evt.invoiceId, arr);
          }
          // Use forEach instead of for...of on Map (ES5 target: downlevelIteration not set).
          eventMap.forEach((arr) => {
            arr.sort((a, b) => {
              if (a.level !== b.level) return a.level - b.level;
              const aTs = (a.createdAt as Timestamp)?.seconds ?? 0;
              const bTs = (b.createdAt as Timestamp)?.seconds ?? 0;
              return aTs - bTs;
            });
          });
          setDunningEventsByInvoice(eventMap);

          // Build dunningEventId → DunningDelivery map (latest delivery wins when multiple exist).
          const deliveryMap = new Map<string, DunningDelivery>();
          for (const del of allDeliveries) {
            const existing = deliveryMap.get(del.dunningEventId);
            if (!existing) {
              deliveryMap.set(del.dunningEventId, del);
            } else {
              // Keep the more recent sentAt
              const existingSecs = (existing.sentAt as Timestamp)?.seconds ?? 0;
              const newSecs = (del.sentAt as Timestamp)?.seconds ?? 0;
              if (newSecs > existingSecs) {
                deliveryMap.set(del.dunningEventId, del);
              }
            }
          }
          setDunningDeliveriesByEventId(deliveryMap);
        } catch (dunningErr: unknown) {
          logger.warn(
            'OpenItemsPage.loadData: failed to load dunning events/deliveries for history ' +
            'display — sub-rows will be empty. Error: ' + String(dunningErr),
          );
          // Leave dunningEventsByInvoice / dunningDeliveriesByEventId as previous values.
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [currentUser, effectiveScopeUid, today, canDunning]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── MA-6: proposals for the Halbautomatik panel ─────────────────────────────
  // Map the already-loaded OP rows into the panel's pure input shape (gate input +
  // display fields + prior events). The panel loads the bank-side data
  // (accounts/imports/transactions) and invoice-settings itself; this keeps the
  // parent delta minimal and the panel testable in isolation (no re-query of
  // invoices/payments). Cheap `useMemo` — recomputed only when rows/events change.
  const dunningProposals: DunningProposalInvoice[] = useMemo(
    () =>
      rows.map((row) => ({
        gate: {
          id: row.invoice.id,
          invoiceNumber: row.invoice.invoiceNumber ?? '',
          isCancelled: row.invoice.isCancelled ?? false,
          isCancellation: row.invoice.isCancellation ?? false,
          invoiceDateISO: extractInvoiceDate(row.invoice),
          dueDateISO: extractDueDate(row.invoice),
          storedGrandTotalCents: row.grandTotalCents,
          paymentsCents: row.payments.map((p) => p.amountCents),
          latestPaymentDateISO: latestPaymentDate(row.payments),
          skontoPercentage: row.invoice.rawData?.skonto?.percentage ?? null,
          skontoDays: row.invoice.rawData?.skonto?.days ?? null,
          buyerEmail: row.invoice.rawData?.buyer?.email ?? null,
        },
        buyerName: row.invoice.buyerName ?? '',
        openAmountCents: row.computed.openAmountCents,
        priorEvents: dunningEventsByInvoice.get(row.invoice.id) ?? [],
      })),
    [rows, dunningEventsByInvoice],
  );

  // ── Derived filtered rows ───────────────────────────────────────────────────

  const filteredRows = rows.filter((r) => {
    if (statusFilter !== 'all' && r.computed.status !== statusFilter) return false;
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      if (
        !r.invoice.invoiceNumber?.toLowerCase().includes(s) &&
        !r.invoice.buyerName?.toLowerCase().includes(s)
      ) {
        return false;
      }
    }
    return true;
  });

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderSummary = () => {
    const overdue = rows.filter((r) => r.computed.status === 'overdue').length;
    const open = rows.filter(
      (r) => r.computed.status === 'open' || r.computed.status === 'partially_paid',
    ).length;
    const totalOpenCents = rows
      .filter(
        (r) =>
          r.computed.status !== 'paid' && r.computed.openAmountCents > 0,
      )
      .reduce((sum, r) => sum + r.computed.openAmountCents, 0);

    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-surface-raised rounded-xl p-4 border border-hairline">
          <p className="text-xs text-muted uppercase tracking-wider mb-1">
            {t('summary.totalOpen')}
          </p>
          <p className="text-2xl font-bold text-primary">
            {formatEuros(totalOpenCents, locale)}
          </p>
        </div>
        <div className="bg-surface-raised rounded-xl p-4 border border-hairline">
          <p className="text-xs text-muted uppercase tracking-wider mb-1">
            {t('summary.openCount')}
          </p>
          <p className="text-2xl font-bold text-primary">{open}</p>
        </div>
        <div className="bg-[var(--tone-danger-bg)] rounded-xl p-4 border border-[var(--tone-danger-border)]">
          <p className="text-xs text-[var(--tone-danger-fg)] uppercase tracking-wider mb-1">
            {t('summary.overdueCount')}
          </p>
          <p className="text-2xl font-bold text-[var(--tone-danger-fg)]">{overdue}</p>
        </div>
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-xl p-4 text-[var(--tone-danger-fg)]">
        <p className="font-medium">{t('error.title')}</p>
        <p className="text-sm mt-1">{error}</p>
        <button
          onClick={loadData}
          className="mt-3 text-sm underline text-[var(--tone-danger-fg)] hover:brightness-110"
        >
          {t('error.retry')}
        </button>
      </div>
    );
  }

  // OP-Take-away (PR-T5, §3.3): bei aktivem Master-Flag verlieren nicht-
  // berechtigte Nutzer den vollen OP-Inhalt. `canOpenItems` ist bei
  // TIER_V2_FLAG=false IMMER true ⇒ dieser Zweig wird nie betreten ⇒
  // byte-identisches Voll-Rendering zum heutigen (ungegateten) OP-Dashboard.
  // PR-T6 (D7): ausgearbeiteter Upsell-Zustand (FeatureTeaser) statt
  // Leer-Platzhalter — NIE Leerrendern.
  if (!canOpenItems) {
    return <OpenItemsTeaser />;
  }

  return (
    <div className="text-primary">
      {/* Page heading */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-primary">{t('pageTitle')}</h1>
        <p className="text-sm text-muted mt-1">{t('pageSubtitle')}</p>
      </div>

      {/* Grace-Window-Countdown (M-G4, architecture-grace-window.md §8.1): NUR
          im Owner-Kontext (`!isReadOnly` — im Mandanten-Modus bezieht sich das
          Fenster auf das EIGENE Abo des StB, dort wäre es irreführend, §6.2).
          Die Komponente selbst ist inert (rendert `null`, wenn kein aktives
          Grace-Fenster/Flag aus/Premium/permanent — §9 Null-Delta). */}
      {!isReadOnly && (
        <GraceCountdownBanner
          sellerData={sellerData}
          feature="openItems"
          featureNameKey="graceBanner.featureOpenItems"
          featureNameDefault="das Offene-Posten-Dashboard"
          promotionId="grace_countdown_open_items"
        />
      )}

      {/* WL-K14 „Geschlossener Kreis" — Plausibilitäts-Hinweise (Doppelzahlung/
          Doppelbeleg/OP-Plausibilität/Skonto-Frist). Outer/Inner-gated (kein
          Fetch für Nicht-Beta-Nutzer), renders null when there is nothing to
          flag. */}
      <ClosedLoopHints />

      {/* Dunning policy settings section — nur wenn der Nutzer das Mahnwesen
          nutzen darf (canDunning, PR-T5) UND die lazy-Komponente gebaut wurde
          (Build-Flag). Bei TIER_V2_FLAG=false identisch zum bisherigen
          DUNNING_ENABLED. */}
      {canDunning && DunningPolicySettings && (
        <div className="mb-6">
          <React.Suspense fallback={null}>
            <DunningPolicySettings />
          </React.Suspense>
        </div>
      )}

      {/* Summary cards */}
      {renderSummary()}

      {/* MA-6 Halbautomatik-Mahnvorschläge — Abschnitt oberhalb der OP-Tabelle
          (architecture-mahn-automatik.md §7.1). Sichtbar nur wenn der Nutzer die
          Halbautomatik nutzen darf (`canUseDunningAutomation`, fail-closed:
          Halbautomatik-Flag ∧ Ultra/Bank-Abgleich ∧ Mahnwesen-Zugriff) UND die
          lazy-Komponente gebaut wurde (Build-Flag `DUNNING_ENABLED`). Das Panel
          rendert intern `null`, solange `automation.mode === 'manual'` ist
          (Default) — die Modus-Prüfung braucht das gelesene Policy-Doc und liegt
          daher im Panel. Es lädt Konten/Importe/Transaktionen/Settings selbst und
          leitet Rechnungen/Events aus den bereits geladenen Parent-Daten ab. */}
      {canUseDunningAutomation(sellerData, currentUser?.uid) && DunningAutomationPanel && (
        <div className="mb-6">
          <React.Suspense fallback={null}>
            <DunningAutomationPanel
              invoices={dunningProposals}
              isReadOnly={isReadOnly}
              onDataChanged={loadData}
            />
          </React.Suspense>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        {/* Search */}
        <input
          type="text"
          placeholder={t('filter.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 bg-surface-raised border border-hairline rounded-lg px-3 py-2 text-sm text-primary placeholder:text-faint focus:outline-none focus:border-indigo-500"
        />

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="bg-surface-raised border border-hairline rounded-lg px-3 py-2 text-sm text-primary focus:outline-none focus:border-indigo-500"
        >
          <option value="all">{t('filter.all')}</option>
          <option value="open">{t('status.open')}</option>
          <option value="partially_paid">{t('status.partially_paid')}</option>
          <option value="paid">{t('status.paid')}</option>
          <option value="overdue">{t('status.overdue')}</option>
        </select>
      </div>

      {/* Table */}
      {filteredRows.length === 0 ? (
        <div className="bg-surface-raised rounded-xl border border-hairline p-8 text-center text-muted">
          {rows.length === 0 ? (
            <div className="py-6">
              <IconOpenItems className="text-gray-600 mx-auto mb-4" size={48} />
              <p className="text-secondary font-medium">{t('empty.noInvoices')}</p>
              <p className="mt-1 text-sm text-muted">{t('empty.noInvoicesSubtext')}</p>
              {/* Offene Posten leiten sich aus Rechnungen ab — bei advisor
                  (read-only) führt der CTA-Zielpfad `/dashboard` (AdvisorHidden)
                  ohnehin auf `/dashboard/archiv` um, daher hier ausgeblendet. */}
              {!isReadOnly && (
                <Button onClick={() => navigate(INVOICE_FORM_ROUTE)} className="mt-5">
                  {t('empty.ctaCreateInvoice')}
                </Button>
              )}
            </div>
          ) : (
            t('empty.noMatch')
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-hairline">
          <table className="w-full text-sm">
            {/* Header styled to match common/DataTable's header language (M-UX-2.4
                Stufe 1 — token classes, not yet the full DataTable component; the
                row bodies below retain bespoke rendering: dunning history sub-rows,
                per-row payment/dunning action buttons). */}
            <thead className="bg-surface-inset text-muted text-xs font-semibold uppercase tracking-wider">
              <tr>
                <th scope="col" className="px-4 py-2 text-left">{t('table.invoiceNumber')}</th>
                <th scope="col" className="px-4 py-2 text-left">{t('table.customer')}</th>
                <th scope="col" className="px-4 py-2 text-right">{t('table.total')}</th>
                <th scope="col" className="px-4 py-2 text-right">{t('table.open')}</th>
                <th scope="col" className="px-4 py-2 text-center">{t('table.status')}</th>
                <th scope="col" className="px-4 py-2 text-left">{t('table.dueDate')}</th>
                <th scope="col" className="sticky right-0 z-10 bg-surface-inset border-l border-default px-4 py-2 text-center">{t('table.action')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--border-hairline)]">
              {filteredRows.map((row) => {
                const dueDate = extractDueDate(row.invoice);
                const isOverdue = row.computed.status === 'overdue';
                const dunningEvents = canDunning
                  ? (dunningEventsByInvoice.get(row.invoice.id) ?? [])
                  : [];

                return (
                  <React.Fragment key={row.invoice.id}>
                  <tr
                    className={`bg-surface hover:bg-surface-raised transition ${
                      isOverdue ? 'border-l-4 border-red-600' : ''
                    }`}
                  >
                    {/* Invoice number */}
                    <td className="px-4 py-2 font-medium text-primary whitespace-nowrap">
                      {row.invoice.invoiceNumber || t('table.unknownNumber')}
                    </td>

                    {/* Customer */}
                    <td className="px-4 py-2 text-secondary">
                      <div className="truncate max-w-[14rem]" title={row.invoice.buyerName || undefined}>
                        {row.invoice.buyerName || '—'}
                      </div>
                    </td>

                    {/* Total */}
                    <td className="px-4 py-2 text-right text-secondary tabular-nums whitespace-nowrap">
                      {formatEuros(row.grandTotalCents, locale)}
                    </td>

                    {/* Open amount — Teilzahlungs-Info (D6) kondensiert als Subzeile,
                        statt in einer eigenen "Bezahlt"-Spalte, die bei den meisten
                        OP-Zeilen ohnehin nur "—" zeigte (architecture-ux-density-v3.md §8.2). */}
                    <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">
                      {row.computed.openAmountCents > 0 ? (
                        <>
                          <span className={isOverdue ? 'text-[var(--tone-danger-fg)] font-medium' : 'text-primary'}>
                            {formatEuros(row.computed.openAmountCents, locale)}
                          </span>
                          {row.paidSumCents > 0 && (
                            <p className="text-xs text-secondary whitespace-nowrap">
                              {t('table.paidPartial', {
                                paid: formatEuros(row.paidSumCents, locale),
                                total: formatEuros(row.grandTotalCents, locale),
                              })}
                            </p>
                          )}
                        </>
                      ) : row.computed.isOverpaid ? (
                        <span className="text-[var(--tone-warning-fg)]">
                          {formatEuros(Math.abs(row.computed.openAmountCents), locale)}{' '}
                          {t('table.overpaidSuffix')}
                        </span>
                      ) : (
                        <span className="text-[var(--tone-success-fg)]">—</span>
                      )}
                    </td>

                    {/* Status badge — trägt seit D6 zusätzlich das 🏦-Badge
                        (Bank-Abgleich), das zuvor in der jetzt entfernten
                        "Bezahlt"-Spalte hing (architecture-ux-density-v3.md §8.2). */}
                    <td className="px-4 py-2 text-center whitespace-nowrap">
                      <StatusBadge
                        status={row.computed.status}
                        isOverpaid={row.computed.isOverpaid}
                      />
                      {row.payments.some((p) => p.source === 'banking-match') && (
                        <span
                          className="ml-1"
                          title={t('table.bankMatched', { defaultValue: 'Per Bank-Abgleich' })}
                          aria-label={t('table.bankMatched', { defaultValue: 'Per Bank-Abgleich' })}
                        >
                          &#x1F3E6;
                        </span>
                      )}
                    </td>

                    {/* Due date — M-B: central, locale-fähiges formatDate ersetzt
                        das rohe ISO-Datum (architecture-ux-density-v2.md §5.6). */}
                    <td className={`px-4 py-2 whitespace-nowrap ${isOverdue ? 'text-[var(--tone-danger-fg)]' : 'text-muted'}`}>
                      {formatDate(dueDate, { locale: i18n.language })}
                    </td>

                    {/* Action — sticky right so payment/dunning actions never scroll off.
                        M-V4-C (architecture-ux-v4.md §2.3, ersetzt v3-Entscheidung D7/D8):
                        "Zahlung erfassen" bleibt sichtbare Primär-RowAction (Daniel-Default
                        "OpenItems: Zahlung primär"), "Mahnung erstellen" wandert als
                        danger-Item ins ⋯-RowActionsMenu (common/RowActionsMenu, M-V4-A).
                        Sichtbarkeits-/Gating-Bedingungen sind EXAKT die des IST, unverändert —
                        leeres items-Array ⇒ RowActionsMenu rendert null (kein toter ⋯-Trigger
                        auf bezahlten Zeilen ohne Mahn-Anlass). */}
                    <td className="sticky right-0 z-10 bg-surface-raised border-l border-default px-4 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {!isReadOnly && row.computed.status !== 'paid' && (
                          <RowAction
                            appearance="icon"
                            variant="accent"
                            icon={<IconEuro />}
                            label={t('table.recordPayment')}
                            data-testid="op-record-payment"
                            onClick={() =>
                              setModalInvoice({
                                id: row.invoice.id,
                                invoiceNumber: row.invoice.invoiceNumber,
                                openAmountCents: Math.max(0, row.computed.openAmountCents),
                              })
                            }
                          />
                        )}
                        {/* Dunning menu item — only when flag is enabled AND row is overdue
                            M11b-3: für advisor (read-only) ausgeblendet (Mahnung = mutierend).
                            Gating-Bedingung identisch zum v3-IST, jetzt Items-Array statt
                            eigener RowAction. */}
                        <RowActionsMenu
                          items={
                            !isReadOnly && canDunning && isOverdue && row.computed.openAmountCents > 0
                              ? ([
                                  {
                                    id: 'op-create-dunning',
                                    label: t('button.createDunning', { ns: 'dunning' }),
                                    icon: <IconNotifications />,
                                    variant: 'danger',
                                    onSelect: () =>
                                      setDunningInvoice({
                                        id: row.invoice.id,
                                        invoiceNumber: row.invoice.invoiceNumber,
                                        invoiceDateISO: extractInvoiceDate(row.invoice),
                                        dueDateISO: extractDueDate(row.invoice),
                                        openAmountCents: Math.max(0, row.computed.openAmountCents),
                                        buyerCompanyName: row.invoice.buyer?.companyName ?? null,
                                      }),
                                  },
                                ] as RowActionsMenuItem[])
                              : []
                          }
                        />
                      </div>
                    </td>
                  </tr>

                  {/* Dunning history sub-row — shown when DUNNING_ENABLED and events exist */}
                  {dunningEvents.length > 0 && (
                    <tr className="bg-surface border-t-0">
                      <td
                        colSpan={7}
                        className="px-6 pt-1.5 pb-3"
                        aria-label={t('history.sectionLabel', { ns: 'dunning' })}
                      >
                        <div className="flex flex-wrap items-start gap-2">
                          <span className="text-xs text-faint mr-1 shrink-0 mt-1.5">
                            {t('history.sectionLabel', { ns: 'dunning' })}:
                          </span>
                          {dunningEvents.map((evt) => (
                            <DunningHistoryChip
                              key={evt.id ?? `${evt.invoiceId}-${evt.level}`}
                              evt={evt}
                              delivery={
                                evt.id ? dunningDeliveriesByEventId.get(evt.id) : undefined
                              }
                              locale={locale}
                              onSendSuccess={loadData}
                            />
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Payment capture modal */}
      {modalInvoice && (
        <PaymentModal
          invoiceId={modalInvoice.id}
          invoiceNumber={modalInvoice.invoiceNumber}
          openAmountCents={modalInvoice.openAmountCents}
          onClose={() => setModalInvoice(null)}
          onSuccess={() => {
            setModalInvoice(null);
            loadData();
          }}
        />
      )}

      {/* Dunning modal — nur wenn der Nutzer das Mahnwesen nutzen darf
          (canDunning, PR-T5), eine Zeile es getriggert hat, und die lazy-
          Komponente gebaut wurde. Bei TIER_V2_FLAG=false identisch zum
          bisherigen DUNNING_ENABLED. */}
      {canDunning && dunningInvoice && DunningModal && (
        <React.Suspense fallback={null}>
          <DunningModal
            invoiceId={dunningInvoice.id}
            invoiceNumber={dunningInvoice.invoiceNumber}
            invoiceDateISO={dunningInvoice.invoiceDateISO}
            dueDateISO={dunningInvoice.dueDateISO}
            openAmountCents={dunningInvoice.openAmountCents}
            buyerCompanyName={dunningInvoice.buyerCompanyName}
            onClose={() => setDunningInvoice(null)}
            onSuccess={() => {
              setDunningInvoice(null);
              loadData();
            }}
          />
        </React.Suspense>
      )}

    </div>
  );
};

export default OpenItemsPage;
